import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import {
  Box,
  Typography,
  Button,
  Container,
  alpha,
} from '@mui/material';
import {
  ArrowForward as ArrowIcon,
  CreditCardOff as NoCreditCardIcon,
  Timer as TimerIcon,
  Autorenew as CancelIcon,
  AutoAwesome as SparkleIcon,
} from '@mui/icons-material';
import { tokens, shadows } from '../../theme';

const HeroSection: React.FC = () => {
  const navigate = useNavigate();
  
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);

  const trustBadges = [
    { icon: <NoCreditCardIcon sx={{ fontSize: 18 }} />, text: 'No credit card required' },
    { icon: <TimerIcon sx={{ fontSize: 18 }} />, text: 'Self-serve OAuth, no sales call' },
    { icon: <CancelIcon sx={{ fontSize: 18 }} />, text: 'Cancel anytime' },
  ];

  return (
    <Box
      component="section"
      sx={{
        position: 'relative',
        minHeight: { xs: 'auto', md: '100vh' },
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        pt: { xs: 12, md: 10 },
        pb: { xs: 8, md: 0 },
      }}
    >
      {/* Animated Background Elements */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(ellipse 80% 50% at 50% -20%, ${alpha(tokens.primary.main, 0.15)}, transparent)`,
          pointerEvents: 'none',
        }}
      />
      
      {/* Floating Orbs */}
      <motion.div
        style={{ y, opacity }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.5 }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            right: '10%',
            width: { xs: 200, md: 400 },
            height: { xs: 200, md: 400 },
            borderRadius: '50%',
            background: `radial-gradient(circle, ${alpha(tokens.primary.main, 0.2)} 0%, transparent 70%)`,
            filter: 'blur(40px)',
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '20%',
            left: '-5%',
            width: { xs: 150, md: 300 },
            height: { xs: 150, md: 300 },
            borderRadius: '50%',
            background: `radial-gradient(circle, ${alpha(tokens.secondary.main, 0.15)} 0%, transparent 70%)`,
            filter: 'blur(40px)',
            pointerEvents: 'none',
          }}
        />
      </motion.div>

      {/* Grid Pattern Overlay */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `
            linear-gradient(${alpha(tokens.border.light, 0.3)} 1px, transparent 1px),
            linear-gradient(90deg, ${alpha(tokens.border.light, 0.3)} 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
          maskImage: 'radial-gradient(ellipse 50% 50% at 50% 50%, black 40%, transparent 100%)',
          opacity: 0.5,
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            alignItems: 'center',
            gap: { xs: 6, lg: 8 },
          }}
        >
          {/* Left Content */}
          <Box sx={{ flex: 1, textAlign: { xs: 'center', lg: 'left' } }}>
            {/* Announcement Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 3,
                  px: 2,
                  py: 1,
                  borderRadius: '100px',
                  background: alpha(tokens.primary.main, 0.08),
                  border: `1px solid ${alpha(tokens.primary.main, 0.15)}`,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: alpha(tokens.primary.main, 0.12),
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <SparkleIcon sx={{ fontSize: 16, color: tokens.primary.main }} />
                <Typography
                  variant="caption"
                  sx={{ color: tokens.primary.main, fontWeight: 600 }}
                >
                  Now with sixty-day revenue projections
                </Typography>
                <ArrowIcon sx={{ fontSize: 14, color: tokens.primary.main }} />
              </Box>
            </motion.div>

            {/* Main Headline */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4rem', lg: '4.5rem' },
                  fontWeight: 800,
                  lineHeight: 1.1,
                  letterSpacing: '-0.03em',
                  color: tokens.text.primary,
                  mb: 3,
                }}
              >
                See what your bar comped and voided.{' '}
                <Box
                  component="span"
                  sx={{
                    display: 'block',
                    background: tokens.primary.gradient,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  And who rang it.
                </Box>
              </Typography>
            </motion.div>

            {/* Subheadline */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Typography
                variant="h5"
                sx={{
                  color: tokens.text.secondary,
                  fontWeight: 400,
                  lineHeight: 1.6,
                  maxWidth: 540,
                  mx: { xs: 'auto', lg: 0 },
                  mb: 4,
                  fontSize: { xs: '1.1rem', md: '1.25rem' },
                }}
              >
                Chairs Up reads your Clover orders and totals comps and voided checks by employee, with average check by day part.
                Where Clover never described a discount on a line, it says so for that employee instead of showing a zero.
              </Typography>
            </motion.div>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                  justifyContent: { xs: 'center', lg: 'flex-start' },
                  mb: 4,
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/signup')}
                  sx={{
                    px: 4,
                    py: 1.75,
                    fontSize: '1rem',
                    fontWeight: 600,
                    borderRadius: '12px',
                    background: tokens.primary.gradient,
                    boxShadow: `0 8px 30px ${alpha(tokens.primary.main, 0.4)}`,
                    '&:hover': {
                      background: tokens.primary.gradient,
                      transform: 'translateY(-3px)',
                      boxShadow: `0 12px 40px ${alpha(tokens.primary.main, 0.5)}`,
                    },
                  }}
                  endIcon={<ArrowIcon />}
                >
                  Connect Clover
                </Button>
              </Box>
            </motion.div>

            {/* Trust Badges */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: { xs: 2, md: 3 },
                  justifyContent: { xs: 'center', lg: 'flex-start' },
                }}
              >
                {trustBadges.map((badge, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      color: tokens.text.secondary,
                    }}
                  >
                    <Box sx={{ color: tokens.success.main }}>{badge.icon}</Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {badge.text}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </motion.div>
          </Box>

          {/* Right Content - Dashboard Preview */}
          <motion.div
            initial={{ opacity: 0, x: 50, rotateY: -10 }}
            animate={{ opacity: 1, x: 0, rotateY: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            style={{ flex: 1.2, perspective: '1000px' }}
          >
            <Box sx={{ position: 'relative' }}>
            <Box
              sx={{
                position: 'relative',
                borderRadius: '20px',
                overflow: 'hidden',
                boxShadow: `0 40px 80px -20px ${alpha(tokens.primary.main, 0.3)}, 0 20px 40px -20px rgba(0,0,0,0.2)`,
                border: `1px solid ${alpha(tokens.border.light, 0.5)}`,
                background: tokens.background.paper,
                transform: { xs: 'none', lg: 'perspective(1000px) rotateY(-5deg) rotateX(2deg)' },
                transition: 'transform 0.5s ease',
                '&:hover': {
                  transform: { xs: 'none', lg: 'perspective(1000px) rotateY(-2deg) rotateX(1deg)' },
                },
              }}
            >
              {/* Mock Dashboard Screenshot */}
              <Box
                sx={{
                  p: 3,
                  background: tokens.background.gradient,
                }}
              >
                {/* Fake Browser Header */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 3,
                    pb: 2,
                    borderBottom: `1px solid ${tokens.border.light}`,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 0.75,
                    }}
                  >
                    {['#FF5F56', '#FFBD2E', '#27CA40'].map((color, i) => (
                      <Box
                        key={i}
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          background: color,
                        }}
                      />
                    ))}
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      mx: 2,
                      px: 2,
                      py: 0.5,
                      borderRadius: '8px',
                      background: tokens.background.subtle,
                      fontSize: '0.75rem',
                      color: tokens.text.secondary,
                      textAlign: 'center',
                    }}
                  >
                    chairsup.com/dashboard
                  </Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: tokens.text.secondary, whiteSpace: 'nowrap' }}>
                    Sample data
                  </Typography>
                </Box>

                {/* Dashboard Content Mock */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 2 }}>
                  {[
                    { label: 'Revenue', value: '$47,892', change: '+12.5%', color: tokens.success.main },
                    { label: 'Customers', value: '2,847', change: '+8.3%', color: tokens.success.main },
                    { label: 'Avg Order', value: '$168', change: '-2.1%', color: tokens.error.main },
                  ].map((stat, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.6 + i * 0.1 }}
                    >
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: '12px',
                          background: tokens.background.paper,
                          border: `1px solid ${tokens.border.light}`,
                        }}
                      >
                        <Typography variant="caption" sx={{ color: tokens.text.secondary }}>
                          {stat.label}
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700, my: 0.5 }}>
                          {stat.value}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: stat.color, fontWeight: 600 }}
                        >
                          {stat.change}
                        </Typography>
                      </Box>
                    </motion.div>
                  ))}
                </Box>

                {/* Chart Placeholder */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.9 }}
                >
                  <Box
                    sx={{
                      p: 3,
                      borderRadius: '12px',
                      background: tokens.background.paper,
                      border: `1px solid ${tokens.border.light}`,
                      height: 160,
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
                      Revenue Trend
                    </Typography>
                    {/* Fake Chart Bars */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        gap: 1,
                        height: 80,
                      }}
                    >
                      {[40, 65, 50, 80, 70, 90, 85, 95, 75, 88, 92, 100].map((h, i) => (
                        <motion.div
                          key={i}
                          initial={{ height: 0 }}
                          animate={{ height: `${h}%` }}
                          transition={{ delay: 1 + i * 0.05, duration: 0.5 }}
                          style={{ flex: 1 }}
                        >
                          <Box
                            sx={{
                              height: '100%',
                              borderRadius: '4px 4px 0 0',
                              background: i === 11 
                                ? tokens.primary.gradient 
                                : alpha(tokens.primary.main, 0.3),
                            }}
                          />
                        </motion.div>
                      ))}
                    </Box>
                  </Box>
                </motion.div>
              </Box>

            </Box>

              {/* AI Insight Floating Card - outside overflow:hidden so it can extend beyond the frame */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8, x: -20 }}
                animate={{ opacity: 1, scale: 1, x: 0 }}
                transition={{ delay: 1.2, duration: 0.5 }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 30,
                    left: -20,
                    p: 2,
                    borderRadius: '14px',
                    background: tokens.background.paper,
                    boxShadow: shadows.xl,
                    border: `1px solid ${tokens.border.light}`,
                    maxWidth: 240,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '8px',
                        background: tokens.success.gradient,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <SparkleIcon sx={{ fontSize: 16, color: 'white' }} />
                    </Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: tokens.success.main }}>
                      AI Insight
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: tokens.text.primary, lineHeight: 1.5 }}>
                    Sample alert. Alert delivery is not switched on yet.
                  </Typography>
                </Box>
              </motion.div>
            </Box>
          </motion.div>
        </Box>
      </Container>
    </Box>
  );
};

export default HeroSection;
